import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../../components/Header";
import AdminBackButton from "../admin/AdminBackButton";
import { authDelete, authFetchBlob, authGet, authPost, authPostForm, getUser, unwrapRows } from "../../lib/authApi";

const DEFAULT_ANALYTE_FORM = {
  analyte_code: "",
  analyte_name: "",
  display_name: "",
  result_type: "qualitative",
  default_result: "",
  negative_label: "",
  positive_label: "",
  unit: "",
  reference_low: "",
  reference_high: "",
  reference_range_text: "",
  cutoff: "",
  upper_cutoff: "",
  critical_low: "",
  critical_high: "",
  default_flag: "",
  positive_flag: "",
  sort_order: "",
  is_reportable: true,
  is_active: true,
  notes: "",
};

const TOX_DEFAULT_CUTOFFS = [
  ["6-MAM", 30, 1500, "Opioids"],
  ["Amphetamine", 100, 5000, "Stimulants"],
  ["Methamphetamine", 100, 5000, "Stimulants"],
  ["Alprazolam", 50, 2500, "Benzodiazepines"],
  ["Diazepam", 50, 2500, "Benzodiazepines"],
  ["EDDP", 50, 2500, "Other"],
  ["Hydrocodone", 50, 2500, "Opioids"],
  ["HydroxyAlprazolam", 50, 2500, "Benzodiazepines"],
  ["Ketamine", 50, 2500, "Dissociatives"],
  ["MDA", 50, 2500, "Stimulants"],
  ["Methadone", 50, 2500, "Other"],
  ["Nortriptyline", 50, 2500, "Antidepressants"],
  ["Oxazepam", 50, 2500, "Benzodiazepines"],
  ["propoxyphene", 50, 2500, "Other"],
  ["Ritalinic Acid", 50, 2500, "Stimulants"],
  ["Tapentadol", 50, 2500, "Opioids"],
  ["Lorazepam", 40, 2000, "Benzodiazepines"],
  ["Nordiazepam", 40, 2000, "Benzodiazepines"],
  ["Temazepam", 40, 2000, "Benzodiazepines"],
  ["Fentanyl", 3, 150, "Opioids"],
  ["Norfentanyl", 3, 150, "Opioids"],
  ["7-Aminoclonazepam", 50, 2500, "Benzodiazepines"],
  ["Gabapentin", 100, 5000, "Gabapentinoids"],
  ["Meprobamate", 100, 5000, "Other"],
  ["Amitriptyline", 50, 2500, "Antidepressants"],
  ["Benzoylecgonine", 50, 2500, "Cocaine"],
  ["Carisoprodol", 50, 2500, "Muscle Relaxants"],
  ["Clonazepam", 50, 2500, "Benzodiazepines"],
  ["Codeine", 50, 2500, "Opioids"],
  ["Cyclobenzaprine", 50, 2500, "Muscle Relaxants"],
  ["Hydromorphone", 50, 2500, "Opioids"],
  ["MDMA", 50, 2500, "Stimulants"],
  ["Meperidine", 50, 2500, "Opioids"],
  ["Methylphenidate", 50, 2500, "Stimulants"],
  ["Morphine", 50, 2500, "Opioids"],
  ["Naloxone", 50, 2500, "Other"],
  ["Norhydrocodone", 50, 2500, "Opioids"],
  ["Noroxycodone", 50, 2500, "Opioids"],
  ["Oxycodone", 50, 2500, "Opioids"],
  ["Oxymorphone", 50, 2500, "Opioids"],
  ["Pregabalin", 50, 2500, "Gabapentinoids"],
  ["Tramadol", 50, 2500, "Opioids"],
  ["Normeperidine", 50, 2500, "Opioids"],
  ["Buprenorphine", 30, 1500, "Opioids"],
  ["Norbuprenorphine", 30, 1500, "Opioids"],
  ["PCP", 30, 1500, "Dissociatives"],
  ["THC-COOH", 30, 1500, "Cannabinoids"],
  ["Zolpidem", 30, 1500, "Sedative-Hypnotics"],
];

function analyteCodeFromName(name) {
  return String(name || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "ANALYTE";
}

function referenceText(cutoff, upperCutoff) {
  const parts = [];
  if (cutoff !== "" && cutoff != null) parts.push(`Negative < ${cutoff} ng/mL; Positive ≥ ${cutoff} ng/mL`);
  if (upperCutoff !== "" && upperCutoff != null) parts.push(`ULOQ ${upperCutoff} ng/mL`);
  return parts.join("; ");
}

function mapToxCutoffToAnalyte(row, department, idx = 0, source = "TOX default") {
  const analyteName = row.analyte || row.analyte_name || row.name || "";
  const cutoff = row.cut_off ?? row.cutoff ?? "";
  const upperCutoff = row.uloq ?? row.upper_cutoff ?? "";
  const drugClass = row.drug_class || row.category || "Other";
  const code = analyteCodeFromName(analyteName);
  return {
    id: row.id ? `tox-cutoff-${row.id}` : `tox-default-${code}`,
    department_code: department?.code || "TOX",
    analyte_code: code,
    analyte_name: analyteName,
    display_name: analyteName,
    result_type: "numeric",
    default_result: department?.default_result || department?.negative_label || "Negative",
    negative_label: department?.negative_label || "Negative",
    positive_label: department?.positive_label || "Positive",
    unit: "ng/mL",
    reference_low: "",
    reference_high: cutoff || "",
    reference_range_text: referenceText(cutoff, upperCutoff),
    cutoff: cutoff || "",
    upper_cutoff: upperCutoff || "",
    critical_low: "",
    critical_high: "",
    default_flag: department?.default_flag || department?.negative_label || "Negative",
    positive_flag: department?.positive_flag || department?.positive_label || "Positive",
    sort_order: row.sort_order ?? idx + 1,
    is_reportable: true,
    is_active: true,
    notes: `Imported from ${source}${drugClass ? `; drug class: ${drugClass}` : ""}.`,
    drug_class: drugClass,
    source: source,
    isLegacyTox: true,
  };
}

function pickRows(resp) {
  return unwrapRows(resp);
}

function compactPayload(form, department) {
  return {
    ...form,
    department_code: department?.code || form.department_code,
    display_name: form.display_name || form.analyte_name,
    negative_label: form.negative_label || department?.negative_label || "Negative",
    positive_label: form.positive_label || department?.positive_label || "Positive",
    default_result: form.default_result || department?.default_result || department?.negative_label || "Negative",
    default_flag: form.default_flag || department?.default_flag || department?.negative_label || "Negative",
    positive_flag: form.positive_flag || department?.positive_flag || department?.positive_label || "Positive",
  };
}

export default function AnalytesPage() {
  const initialOrgId = new URLSearchParams(window.location.search).get("org") || "";
  const initialDepartmentId = new URLSearchParams(window.location.search).get("department") || "";

  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState(initialOrgId);
  const [departments, setDepartments] = useState([]);
  const [departmentId, setDepartmentId] = useState(initialDepartmentId);
  const [analytes, setAnalytes] = useState([]);
  const [legacyToxCutoffs, setLegacyToxCutoffs] = useState([]);
  const [selectedAnalyteId, setSelectedAnalyteId] = useState("");
  const [form, setForm] = useState(DEFAULT_ANALYTE_FORM);
  const [quickFilter, setQuickFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [toast, setToast] = useState({ open: false, msg: "", severity: "success" });

  const role = String(getUser()?.role || localStorage.getItem("role") || "").toLowerCase();
  const canWrite = ["owner", "global_admin", "superadmin", "admin"].includes(role);
  const selectedDepartment = useMemo(() => departments.find((d) => d.id === departmentId) || null, [departments, departmentId]);
  const isToxDepartment = String(selectedDepartment?.code || "").trim().toUpperCase() === "TOX";

  const syncUrl = (nextOrgId = orgId, nextDepartmentId = departmentId) => {
    const params = new URLSearchParams();
    if (nextOrgId) params.set("org", nextOrgId);
    if (nextDepartmentId) params.set("department", nextDepartmentId);
    const query = params.toString();
    window.history.replaceState(null, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
  };

  const loadOrgs = async () => {
    setLoading(true);
    setErr("");
    try {
      const resp = await authGet("/api/admin/organizations");
      const rows = pickRows(resp);
      setOrgs(rows);
      if (!orgId && rows.length) setOrgId(rows[0].id);
    } catch (e) {
      setErr(e.message || "Failed to load organizations.");
      setOrgs([]);
    } finally {
      setLoading(false);
    }
  };

  const loadDepartments = async (targetOrgId = orgId) => {
    if (!targetOrgId) return;
    setLoading(true);
    setErr("");
    try {
      const resp = await authGet(`/api/admin/organizations/${encodeURIComponent(targetOrgId)}/departments`);
      const rows = pickRows(resp).filter((d) => d.is_active !== false);
      setDepartments(rows);
      const nextDepartment =
        rows.find((d) => d.id === departmentId) ||
        rows.find((d) => String(d.code || "").toUpperCase() === "TOX") ||
        rows[0] ||
        null;
      setDepartmentId(nextDepartment?.id || "");
      if (!nextDepartment) setAnalytes([]);
    } catch (e) {
      setErr(e.message || "Failed to load departments.");
      setDepartments([]);
      setDepartmentId("");
      setAnalytes([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytes = async (targetOrgId = orgId, targetDepartmentId = departmentId) => {
    if (!targetOrgId || !targetDepartmentId) return;
    setLoading(true);
    setErr("");
    try {
      const resp = await authGet(
        `/api/admin/organizations/${encodeURIComponent(targetOrgId)}/departments/${encodeURIComponent(targetDepartmentId)}/analytes`
      );
      setAnalytes(pickRows(resp).map((row) => ({ ...row, source: "Department analyte", isLegacyTox: false })));
      setSelectedAnalyteId("");
      setForm(DEFAULT_ANALYTE_FORM);
    } catch (e) {
      setErr(e.message || "Failed to load analytes.");
      setAnalytes([]);
    } finally {
      setLoading(false);
    }
  };

  const loadLegacyToxCutoffs = async (targetOrgId = orgId) => {
    if (!targetOrgId) return;
    try {
      const resp = await authGet(`/api/tox-cutoffs?org_id=${encodeURIComponent(targetOrgId)}`);
      setLegacyToxCutoffs(pickRows(resp));
    } catch (e) {
      // Keep hardcoded TOX defaults visible even if the legacy org cutoffs endpoint is unavailable.
      setLegacyToxCutoffs([]);
    }
  };

  useEffect(() => {
    loadOrgs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (orgId) {
      syncUrl(orgId, "");
      loadDepartments(orgId);
      loadLegacyToxCutoffs(orgId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  useEffect(() => {
    if (orgId && departmentId) {
      syncUrl(orgId, departmentId);
      loadAnalytes(orgId, departmentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId]);

  const toxSourceRows = useMemo(() => {
    if (!isToxDepartment) return [];
    const fromLegacyDb = legacyToxCutoffs.map((row, idx) => mapToxCutoffToAnalyte(row, selectedDepartment, idx, "Legacy TOX cutoffs"));
    const byCode = new Map();
    TOX_DEFAULT_CUTOFFS.forEach(([analyte, cutoff, uloq, drugClass], idx) => {
      const mapped = mapToxCutoffToAnalyte({ analyte, cut_off: cutoff, uloq, drug_class: drugClass }, selectedDepartment, idx, "Hardcoded TOX menu");
      byCode.set(mapped.analyte_code, mapped);
    });
    fromLegacyDb.forEach((mapped) => byCode.set(mapped.analyte_code, mapped));
    return Array.from(byCode.values());
  }, [isToxDepartment, legacyToxCutoffs, selectedDepartment]);

  const displayedAnalytes = useMemo(() => {
    if (!isToxDepartment) return analytes;
    const savedCodes = new Set(analytes.map((row) => String(row.analyte_code || "").toUpperCase()));
    const savedNames = new Set(analytes.map((row) => String(row.analyte_name || "").trim().toLowerCase()));
    const missingToxRows = toxSourceRows.filter((row) => !savedCodes.has(row.analyte_code) && !savedNames.has(String(row.analyte_name || "").trim().toLowerCase()));
    return [...analytes, ...missingToxRows];
  }, [analytes, isToxDepartment, toxSourceRows]);

  const filtered = useMemo(() => {
    const q = quickFilter.trim().toLowerCase();
    const rows = displayedAnalytes;
    if (!q) return rows;
    return rows.filter((row) =>
      [
        row.department_code,
        row.analyte_code,
        row.analyte_name,
        row.display_name,
        row.result_type,
        row.unit,
        row.reference_range_text,
        row.cutoff,
        row.upper_cutoff,
        row.drug_class,
        row.source,
        row.notes,
      ]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [displayedAnalytes, quickFilter]);

  const onSelectRow = (row) => {
    setSelectedAnalyteId(row.id || "");
    setForm({
      analyte_code: row.analyte_code || "",
      analyte_name: row.analyte_name || "",
      display_name: row.display_name || "",
      result_type: row.result_type || "qualitative",
      default_result: row.default_result || "",
      negative_label: row.negative_label || "",
      positive_label: row.positive_label || "",
      unit: row.unit || "",
      reference_low: row.reference_low ?? "",
      reference_high: row.reference_high ?? "",
      reference_range_text: row.reference_range_text || "",
      cutoff: row.cutoff ?? "",
      upper_cutoff: row.upper_cutoff ?? "",
      critical_low: row.critical_low ?? "",
      critical_high: row.critical_high ?? "",
      default_flag: row.default_flag || "",
      positive_flag: row.positive_flag || "",
      sort_order: row.sort_order ?? "",
      is_reportable: row.is_reportable !== false,
      is_active: row.is_active !== false,
      notes: row.notes || "",
    });
  };

  const resetForm = () => {
    setSelectedAnalyteId("");
    setForm(DEFAULT_ANALYTE_FORM);
  };

  const saveAnalyte = async () => {
    if (!canWrite) return;
    if (!orgId) return setErr("Select an organization first.");
    if (!departmentId) return setErr("Select a department first.");
    if (!form.analyte_code.trim()) return setErr("Analyte code is required.");
    if (!form.analyte_name.trim()) return setErr("Analyte name is required.");

    setLoading(true);
    setErr("");
    try {
      await authPost(
        `/api/admin/organizations/${encodeURIComponent(orgId)}/departments/${encodeURIComponent(departmentId)}/analytes`,
        compactPayload(form, selectedDepartment)
      );
      setToast({ open: true, msg: selectedAnalyteId ? "Analyte updated." : "Analyte added.", severity: "success" });
      await loadAnalytes(orgId, departmentId);
    } catch (e) {
      setErr(e.message || "Failed to save analyte.");
    } finally {
      setLoading(false);
    }
  };

  const removeAnalyte = async (row) => {
    if (!canWrite || !orgId || !departmentId || !row?.id) return;
    const ok = window.confirm(`Remove ${row.analyte_name || row.analyte_code} from ${selectedDepartment?.code || "this department"}?`);
    if (!ok) return;
    setLoading(true);
    setErr("");
    try {
      await authDelete(
        `/api/admin/organizations/${encodeURIComponent(orgId)}/departments/${encodeURIComponent(departmentId)}/analytes/${encodeURIComponent(row.id)}`
      );
      setToast({ open: true, msg: "Analyte removed.", severity: "success" });
      await loadAnalytes(orgId, departmentId);
    } catch (e) {
      setErr(e.message || "Failed to remove analyte.");
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async () => {
    if (!orgId || !departmentId) return;
    try {
      const blob = await authFetchBlob(
        `/api/admin/organizations/${encodeURIComponent(orgId)}/departments/${encodeURIComponent(departmentId)}/analytes/template.csv`
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${selectedDepartment?.code || "department"}-analytes-template.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e.message || "Template download failed.");
    }
  };

  const importCsv = async (file) => {
    if (!canWrite || !file || !orgId || !departmentId) return;
    setLoading(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await authPostForm(
        `/api/admin/organizations/${encodeURIComponent(orgId)}/departments/${encodeURIComponent(departmentId)}/analytes/import`,
        fd
      );
      setToast({ open: true, msg: `Imported ${resp.imported || 0} analytes.`, severity: "success" });
      await loadAnalytes(orgId, departmentId);
    } catch (e) {
      setErr(e.message || "CSV import failed.");
    } finally {
      setLoading(false);
    }
  };

  const importMissingToxDefaults = async () => {
    if (!canWrite || !orgId || !departmentId || !isToxDepartment) return;
    const savedCodes = new Set(analytes.map((row) => String(row.analyte_code || "").toUpperCase()));
    const savedNames = new Set(analytes.map((row) => String(row.analyte_name || "").trim().toLowerCase()));
    const missing = toxSourceRows.filter((row) => !savedCodes.has(row.analyte_code) && !savedNames.has(String(row.analyte_name || "").trim().toLowerCase()));
    if (!missing.length) {
      setToast({ open: true, msg: "All TOX defaults are already saved for this department.", severity: "info" });
      return;
    }

    setLoading(true);
    setErr("");
    try {
      for (const row of missing) {
        await authPost(
          `/api/admin/organizations/${encodeURIComponent(orgId)}/departments/${encodeURIComponent(departmentId)}/analytes`,
          compactPayload(row, selectedDepartment)
        );
      }
      setToast({ open: true, msg: `Restored ${missing.length} TOX analytes with cutoff/reference ranges.`, severity: "success" });
      await loadAnalytes(orgId, departmentId);
    } catch (e) {
      setErr(e.message || "Failed to restore TOX defaults.");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { field: "department_code", headerName: "Dept", width: 90 },
    { field: "analyte_code", headerName: "Code", width: 150 },
    { field: "analyte_name", headerName: "Analyte", flex: 1, minWidth: 190 },
    { field: "display_name", headerName: "Display", flex: 1, minWidth: 180 },
    { field: "result_type", headerName: "Type", width: 120 },
    { field: "drug_class", headerName: "Drug Class", width: 160 },
    { field: "unit", headerName: "Unit", width: 90 },
    { field: "reference_range_text", headerName: "Reference Range", width: 170 },
    { field: "cutoff", headerName: "Cutoff", width: 110 },
    { field: "upper_cutoff", headerName: "Upper Cutoff / ULOQ", width: 160 },
    { field: "sort_order", headerName: "Sort", width: 90 },
    { field: "is_reportable", headerName: "Report", width: 95, type: "boolean" },
    { field: "is_active", headerName: "Active", width: 95, type: "boolean" },
    { field: "source", headerName: "Source", width: 170 },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) =>
        canWrite && !params.row.isLegacyTox ? (
          <Button size="small" color="error" onClick={(e) => { e.stopPropagation(); removeAnalyte(params.row); }}>
            Remove
          </Button>
        ) : null,
    },
  ];

  return (
    <Box sx={{ flex: "1 1 auto", minHeight: 0, minWidth: 0, height: "100%", overflow: "auto", p: 3, pb: 6 }}>
      <AdminBackButton />
      <Header title="Analytes" subtitle="View, add, import, and manage analyte data by organization and department" />

      {err ? <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert> : null}
      {!canWrite ? <Alert severity="warning" sx={{ mb: 2 }}>Your role can view analytes, but only Admin or Global Admin roles can add, import, or remove them.</Alert> : null}

      <Card sx={{ mb: 2 }} variant="outlined">
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Organization</InputLabel>
                <Select label="Organization" value={orgId || ""} onChange={(e) => { setOrgId(e.target.value); setDepartmentId(""); }}>
                  {orgs.map((org) => (
                    <MenuItem key={org.id} value={org.id}>{org.name}{org.code ? ` (${org.code})` : ""}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Department</InputLabel>
                <Select label="Department" value={departmentId || ""} onChange={(e) => setDepartmentId(e.target.value)} disabled={!orgId || !departments.length}>
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>{dept.code} — {dept.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Search analyte data"
                value={quickFilter}
                onChange={(e) => setQuickFilter(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
                <Button variant="outlined" onClick={() => loadAnalytes(orgId, departmentId)} disabled={loading || !orgId || !departmentId}>
                  Refresh
                </Button>
                <Button variant="outlined" onClick={downloadTemplate} disabled={loading || !orgId || !departmentId}>
                  Download Template
                </Button>
                <Button variant="contained" component="label" disabled={loading || !canWrite || !orgId || !departmentId}>
                  Upload Completed Template / CSV
                  <input
                    hidden
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(e) => {
                      importCsv(e.target.files?.[0]);
                      e.target.value = "";
                    }}
                  />
                </Button>
                {isToxDepartment ? (
                  <Button variant="contained" color="secondary" onClick={importMissingToxDefaults} disabled={loading || !canWrite || !orgId || !departmentId}>
                    Restore Missing TOX Analytes
                  </Button>
                ) : null}
                {selectedDepartment ? <Chip label={`${selectedDepartment.code}: ${selectedDepartment.name}`} color="primary" variant="outlined" /> : null}
                <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                  Showing {filtered.length} of {displayedAnalytes.length} analytes{isToxDepartment ? ` (${analytes.length} saved, ${Math.max(displayedAnalytes.length - analytes.length, 0)} TOX defaults/legacy)` : ""}
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>Analyte Data</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select an organization and department to view all configured analytes. For TOX, the page also shows legacy/hardcoded menu analytes with cutoff/reference ranges so they are not lost; use Restore Missing TOX Analytes to save them into the department. Click a row to load it into the form for review or update.
              </Typography>
              <Box sx={{ height: 650, width: "100%" }}>
                <DataGrid
                  rows={filtered}
                  columns={columns}
                  loading={loading}
                  getRowId={(r) => r.id}
                  onRowClick={(params) => onSelectRow(params.row)}
                  rowSelectionModel={selectedAnalyteId ? [selectedAnalyteId] : []}
                  disableRowSelectionOnClick={false}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>{selectedAnalyteId ? "Update Analyte" : "Add Analyte"}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                New analytes are saved under the selected organization and department. Use cutoff/reference fields only when that department needs them.
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={5} lg={12}>
                  <TextField label="Analyte Code" fullWidth size="small" value={form.analyte_code} onChange={(e) => setForm((s) => ({ ...s, analyte_code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "_") }))} disabled={!canWrite} />
                </Grid>
                <Grid item xs={12} md={7} lg={12}>
                  <TextField label="Analyte Name" fullWidth size="small" value={form.analyte_name} onChange={(e) => setForm((s) => ({ ...s, analyte_name: e.target.value }))} disabled={!canWrite} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Display Name" fullWidth size="small" value={form.display_name} onChange={(e) => setForm((s) => ({ ...s, display_name: e.target.value }))} disabled={!canWrite} />
                </Grid>
                <Grid item xs={12} md={6} lg={12}>
                  <TextField select label="Result Type" fullWidth size="small" value={form.result_type} onChange={(e) => setForm((s) => ({ ...s, result_type: e.target.value }))} disabled={!canWrite}>
                    <MenuItem value="qualitative">Qualitative</MenuItem>
                    <MenuItem value="numeric">Numeric</MenuItem>
                    <MenuItem value="text">Text</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6} lg={12}>
                  <TextField label="Unit" fullWidth size="small" value={form.unit} onChange={(e) => setForm((s) => ({ ...s, unit: e.target.value }))} disabled={!canWrite} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField label="Default Result" fullWidth size="small" value={form.default_result} onChange={(e) => setForm((s) => ({ ...s, default_result: e.target.value }))} disabled={!canWrite} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField label="Positive Label" fullWidth size="small" value={form.positive_label} onChange={(e) => setForm((s) => ({ ...s, positive_label: e.target.value }))} disabled={!canWrite} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField label="Cutoff" fullWidth size="small" value={form.cutoff} onChange={(e) => setForm((s) => ({ ...s, cutoff: e.target.value }))} disabled={!canWrite} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField label="Upper Cutoff / ULOQ" fullWidth size="small" value={form.upper_cutoff} onChange={(e) => setForm((s) => ({ ...s, upper_cutoff: e.target.value }))} disabled={!canWrite} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField label="Ref Low" fullWidth size="small" value={form.reference_low} onChange={(e) => setForm((s) => ({ ...s, reference_low: e.target.value }))} disabled={!canWrite} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField label="Ref High" fullWidth size="small" value={form.reference_high} onChange={(e) => setForm((s) => ({ ...s, reference_high: e.target.value }))} disabled={!canWrite} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Reference Range Text" fullWidth size="small" value={form.reference_range_text} onChange={(e) => setForm((s) => ({ ...s, reference_range_text: e.target.value }))} disabled={!canWrite} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField label="Sort Order" fullWidth size="small" value={form.sort_order} onChange={(e) => setForm((s) => ({ ...s, sort_order: e.target.value }))} disabled={!canWrite} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={1}>
                    <FormControlLabel control={<Checkbox checked={form.is_reportable} onChange={(e) => setForm((s) => ({ ...s, is_reportable: e.target.checked }))} disabled={!canWrite} />} label="Report" />
                    <FormControlLabel control={<Checkbox checked={form.is_active} onChange={(e) => setForm((s) => ({ ...s, is_active: e.target.checked }))} disabled={!canWrite} />} label="Active" />
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Notes" fullWidth size="small" multiline minRows={2} value={form.notes} onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))} disabled={!canWrite} />
                </Grid>
                <Grid item xs={12}>
                  <Button variant="contained" onClick={saveAnalyte} disabled={loading || !canWrite || !orgId || !departmentId} sx={{ mr: 1 }}>
                    {selectedAnalyteId ? "Update Analyte" : "Add Analyte"}
                  </Button>
                  <Button variant="outlined" onClick={resetForm} disabled={loading}>Clear</Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar open={toast.open} autoHideDuration={4000} onClose={() => setToast((t) => ({ ...t, open: false }))}>
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))} sx={{ width: "100%" }}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
